import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	Output,
	EventEmitter,
	OnChanges
} from "@angular/core";
import { IdService } from "../shared/services/id.service";

import { IPointEvent } from "./point.interface";

@Component({
	selector: "rbc-point",
	template: `
	<ng-content></ng-content>
		<div class="rbc-point">
			<button
				[attr.aria-label]= "ariaLabel ? ariaLabel : label"
				[attr.aria-describedby] = "ariaDescribedBy"
				[attr.aria-pressed]= "ariaPressed"
				[attr.aria-labelledby]= "(!number || (number && completed)) && label ?  id + 'Label' : null"
				[id]= "id"
				[ngClass]="{
					active: checked,
					disabled: disabled,
					completed: !checked && completed,
					passed: !checked && passed
				}"
				class = "rbc-point-circle"
				(click) = "toggleActiveState()"
				[disabled] = "disabled"
			>
				<rbc-icon *ngIf="!checked && completed" icon="check" type="solid"></rbc-icon>
				<span *ngIf="number && (checked || !completed)" class="rbc-point-circle-number">
					{{number}}
				</span>
			</button>

			<div *ngIf="label || helpText" class="rbc-point-content">
				<span
					[id]="id + 'Label'"
					*ngIf="label"
					[class.disabled]="disabled"
					class="rbc-point-label"
				>
					{{ label }}
				</span>

				<!-- Help text -->

				<rbc-help-text
					[id]="id + 'HelpText'"
					*ngIf="helpText"
					[text]="helpText"
					[wrapperClass]="'rbc-point-help-text'"
				></rbc-help-text>
			</div>
		</div>
	`,
	styles: []
})
export class PointComponent implements OnInit, OnDestroy, OnChanges {
	/**
	 * Point button ID.
	 */
	@Input() id: string;

	/**
	 * Label.
	 */
	@Input() label: string;

	/**
	 * Help text.
	 */
	@Input() helpText: string;

	/**
	 * Point number.
	 */
	@Input() number: string | number;

	/**
	 * Disable point.
	 */
	@Input() disabled: string | boolean;

	/**
	 * Make Point active.
	 *
	 * @todo renamed "checked" to be "active"
	 */
	@Input() checked: string | boolean;

	/**
	 * Mark Point as completed.
	 */
	@Input() completed: string | boolean;

	/**
	 * Mark Point as skipped/passed.
	 */
	@Input() passed: string | boolean;

	/**
	 * Allow to change "active" state of the Point.
	 */
	@Input() editable = true;

	/**
	 * Aria label.
	 */
	@Input() ariaLabel: string;

	/**
	 * Point Update event.
	 */
	@Output() pointUpdate = new EventEmitter<IPointEvent>();

	/**
	 * Aria "described-by" point button property.
	 */
	public ariaDescribedBy: string;

	/**
	 * Aria pressed value.
	 */
	public ariaPressed: string;

	constructor(private idService: IdService) { }

	/**
	 * Set component ID and aria attributes.
	 */
	public ngOnInit(): void {
		this.id = this.id ? this.id : this.idService.generate();

		this.updateAriaAttributes();
		this.firePointUpdate();
	}

	/**
	 * Remove Point button ID from ID service.
	 */
	public ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	/**
	 * Update aria attributes & fire point update event.
	 */
	ngOnChanges(): void {
		this.updateAriaAttributes();

		this.firePointUpdate();
	}

	/**
	 * Toggle active state.
	 */
	public toggleActiveState(): void {
		if (!this.disabled && this.editable) {
			this.checked = !this.checked;

			// Trigger changes manually.
			this.updateAriaAttributes();
			this.firePointUpdate();
		}
	}

	/**
	 * Fire Point Update event.
	 *
	 * This includes when the checked, completed, disabled, passed values have been updated.
	 */
	private firePointUpdate(): void {
		this.pointUpdate.emit({
			id: this.id,
			active: this.checked,
			completed: this.completed,
			passed: this.passed,
			disabled: this.disabled
		});
	}

	/**
	 * Update ARIA attributes.
	 */
	private updateAriaAttributes(): void {
		this.ariaPressed = this.getAriaPressedValue();
		this.ariaDescribedBy = this.getAriaDescribedBy();
	}

	/**
	 * Get "described-by" aria attribute value.
	 */
	private getAriaDescribedBy(): string {
		const ids = [
			this.label ? `${this.id}Label` : null, // Label ID.
			this.helpText ? `${this.id}HelpText` : null // Help text ID.
		].filter(id => id);

		return ids.length ? ids.join(" ") : null;
	}

	/**
	 * Check if Point button is pressed or not.
	 */
	private getAriaPressedValue(): string {
		if (this.completed) {
			return "true";
		} else if (this.checked) {
			return "mixed";
		} else {
			return "false";
		}
	}
}
